import type { LibraryEntry, LibrarySyncMutation, MediaSummary, PlaybackRecord, SyncPayload } from './contracts'

const MAX_FAVORITES = 200
export const MAX_HISTORY_ITEMS = 300
export const MAX_SYNC_MUTATIONS = 600
const MAX_TEXT = 800

function text(value: unknown, max = MAX_TEXT): string | undefined {
    if (typeof value !== 'string') { return undefined }
    const trimmed = value.trim()
    return trimmed ? trimmed.slice(0, max) : undefined
}

/** Keep library records navigable without persisting detail payloads or playback URLs. */
export function compactMediaSummary(value: unknown): MediaSummary | undefined {
    if (!value || typeof value !== 'object') { return undefined }
    const raw = value as Partial<MediaSummary>
    const id = text(raw.id, 180)
    const sourceId = text(raw.sourceId, 80)
    const sourceName = text(raw.sourceName, 120)
    const title = text(raw.title, 240)
    if (!id || !sourceId || !sourceName || !title) { return undefined }
    const result: MediaSummary = { id, sourceId, sourceName, title, kind: raw.kind === 'movie' || raw.kind === 'series' || raw.kind === 'variety' || raw.kind === 'anime' ? raw.kind : 'unknown' }
    const optional: Array<[keyof MediaSummary, unknown, number]> = [
        ['originalTitle', raw.originalTitle, 240],
        ['poster', raw.poster, 2_000],
        ['backdrop', raw.backdrop, 2_000],
        ['year', raw.year, 30],
        ['remarks', raw.remarks, 120],
        ['category', raw.category, 120],
        ['score', raw.score, 30],
        ['runtime', raw.runtime, 60],
        ['description', raw.description, MAX_TEXT],
    ]
    for (const [key, value, max] of optional) {
        const normalized = text(value, max)
        if (normalized) { (result as unknown as Record<string, unknown>)[key] = normalized }
    }
    if (Number.isFinite(raw.episodeCount) && Number(raw.episodeCount) >= 0) { result.episodeCount = Math.min(Number(raw.episodeCount), 10_000) }
    if (Array.isArray(raw.alternatives)) {
        result.alternatives = raw.alternatives.slice(0, 20).flatMap((item) => {
            if (!item || typeof item !== 'object') { return [] }
            const alternative = item as unknown as Record<string, unknown>
            const alternativeId = text(alternative.id, 180)
            const alternativeSourceId = text(alternative.sourceId, 80)
            const alternativeSourceName = text(alternative.sourceName, 120)
            if (!alternativeId || !alternativeSourceId || !alternativeSourceName) { return [] }
            return [{ id: alternativeId, sourceId: alternativeSourceId, sourceName: alternativeSourceName, episodeCount: Number.isFinite(alternative.episodeCount) ? Math.min(Number(alternative.episodeCount), 10_000) : undefined }]
        })
    }
    return result
}

function compactEntry(value: unknown): LibraryEntry | undefined {
    if (!value || typeof value !== 'object') { return undefined }
    const raw = value as Partial<LibraryEntry>
    const media = compactMediaSummary(raw.media)
    const key = text(raw.key, 280)
    if (!media || !key) { return undefined }
    return { key, media, updatedAt: Number.isFinite(raw.updatedAt) ? Number(raw.updatedAt) : 0 }
}

function compactHistory(value: unknown): PlaybackRecord | undefined {
    const entry = compactEntry(value)
    if (!entry || !value || typeof value !== 'object') { return undefined }
    const raw = value as Partial<PlaybackRecord>
    const position = Number(raw.position)
    const duration = Number(raw.duration)
    return { ...entry, episodeIndex: Number.isFinite(raw.episodeIndex) ? Math.max(0, Math.floor(Number(raw.episodeIndex))) : 0, episodeLabel: text(raw.episodeLabel, 180) || '', position: Number.isFinite(position) ? Math.max(0, Math.min(position, 86_400_000)) : 0, duration: Number.isFinite(duration) ? Math.max(0, Math.min(duration, 86_400_000)) : 0, progress: Number.isFinite(raw.progress) ? Math.max(0, Math.min(Number(raw.progress), 1)) : 0 }
}

export function compactSyncPayload(value: unknown): SyncPayload {
    const raw = value && typeof value === 'object' ? value as Partial<SyncPayload> : {}
    const favorites = Array.isArray(raw.favorites) ? raw.favorites.slice(0, MAX_FAVORITES).flatMap((item) => { const entry = compactEntry(item); return entry ? [entry] : [] }) : []
    const history = Array.isArray(raw.history)
        ? raw.history.flatMap((item) => { const entry = compactHistory(item); return entry ? [entry] : [] }).sort((left, right) => right.updatedAt - left.updatedAt).slice(0, MAX_HISTORY_ITEMS)
        : []
    return { favorites, history, updatedAt: Number.isFinite(raw.updatedAt) ? Number(raw.updatedAt) : Date.now() }
}

export function compactLibrarySyncMutations(value: unknown): LibrarySyncMutation[] {
    if (!Array.isArray(value)) { return [] }
    return value.slice(0, MAX_SYNC_MUTATIONS).flatMap((candidate): LibrarySyncMutation[] => {
        if (!candidate || typeof candidate !== 'object') { return [] }
        const raw = candidate as Partial<LibrarySyncMutation>
        const id = raw.id === undefined ? undefined : text(raw.id, 80)
        if (raw.id !== undefined && !id) { return [] }
        const identity = id ? { id } : {}
        if (raw.type === 'favorite-upsert') {
            const entry = compactEntry(raw.entry)
            return entry ? [{ ...identity, type: raw.type, entry }] : []
        }
        if (raw.type === 'history-upsert') {
            const record = compactHistory(raw.record)
            return record ? [{ ...identity, type: raw.type, record }] : []
        }
        if (raw.type === 'favorite-delete' || raw.type === 'history-delete') {
            const key = text(raw.key, 280)
            return key ? [{ ...identity, type: raw.type, key }] : []
        }
        return raw.type === 'history-clear' ? [{ ...identity, type: raw.type }] : []
    })
}

export function coalesceLibrarySyncMutations(mutations: LibrarySyncMutation[], next: LibrarySyncMutation): LibrarySyncMutation[] {
    if (next.type === 'history-clear') {
        return [...mutations.filter(mutation => !mutation.type.startsWith('history-')), next]
    }
    if (next.type === 'favorite-upsert' || next.type === 'favorite-delete') {
        const key = next.type === 'favorite-upsert' ? next.entry.key : next.key
        return [...mutations.filter((mutation) => {
            if (mutation.type === 'favorite-upsert') { return mutation.entry.key !== key }
            return mutation.type !== 'favorite-delete' || mutation.key !== key
        }), next]
    }
    const key = next.type === 'history-upsert' ? next.record.key : next.key
    return [...mutations.filter((mutation) => {
        if (mutation.type === 'history-upsert') { return mutation.record.key !== key }
        return mutation.type !== 'history-delete' || mutation.key !== key
    }), next]
}

export function applyLibrarySyncMutations(value: unknown, mutations: LibrarySyncMutation[], updatedAt = Date.now()): SyncPayload {
    const current = compactSyncPayload(value)
    const favorites = new Map(current.favorites.map(entry => [entry.key, entry]))
    const history = new Map(current.history.map(record => [record.key, record]))

    for (const mutation of compactLibrarySyncMutations(mutations)) {
        if (mutation.type === 'favorite-upsert') {
            favorites.set(mutation.entry.key, { ...mutation.entry, updatedAt })
        }
        else if (mutation.type === 'favorite-delete') { favorites.delete(mutation.key) }
        else if (mutation.type === 'history-upsert') {
            history.set(mutation.record.key, { ...mutation.record, updatedAt })
        }
        else if (mutation.type === 'history-delete') { history.delete(mutation.key) }
        else { history.clear() }
    }

    return compactSyncPayload({
        favorites: [...favorites.values()].sort((left, right) => right.updatedAt - left.updatedAt),
        history: [...history.values()],
        updatedAt,
    })
}
